import { useEffect, useState } from "react";
import { Inbox, Mail, Phone, Building2, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const STATUSES = ["new", "contacted", "won", "closed"];
const statusColor = {
  new: "bg-primary/15 text-primary border-primary/30",
  contacted: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  won: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  closed: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/leads/${id}`, { status });
    setLeads((l) => l.map((x) => (x.id === id ? { ...x, status } : x)));
    toast.success("Status updated");
  };

  const remove = async (id) => {
    await api.delete(`/admin/leads/${id}`);
    setLeads((l) => l.filter((x) => x.id !== id));
    toast.success("Lead deleted");
  };

  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: leads.filter((l) => l.status === s).length }), {});

  return (
    <div data-testid="admin-leads">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[{ k: "total", v: leads.length, l: "Total leads" }, ...STATUSES.map((s) => ({ k: s, v: counts[s], l: s.charAt(0).toUpperCase() + s.slice(1) }))].slice(0, 4).map((c) => (
          <div key={c.k} className="border border-white/10 bg-[#0F111A] p-5">
            <p className="font-heading text-3xl font-semibold text-white">{c.v}</p>
            <p className="text-sm text-slate-400 mt-1">{c.l}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-5">
        <Inbox className="w-5 h-5 text-primary" />
        <h2 className="font-heading text-xl font-semibold">Inbound Leads</h2>
      </div>

      {loading ? (
        <div className="py-20 grid place-items-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : leads.length === 0 ? (
        <div className="border border-white/10 bg-[#0F111A] p-16 text-center text-slate-500">
          <Inbox className="w-10 h-10 mx-auto mb-4 opacity-50" />
          No leads yet. Submissions from the contact form will appear here.
        </div>
      ) : (
        <div className="space-y-4">
          {leads.map((lead) => (
            <div key={lead.id} data-testid={`lead-${lead.id}`} className="border border-white/10 bg-[#0F111A] p-6 hover:border-white/20 transition-colors">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-semibold text-white">{lead.name}</h3>
                    <span className={`text-xs px-2.5 py-0.5 border ${statusColor[lead.status] || statusColor.new}`}>{lead.status}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-slate-400">
                    <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1.5 hover:text-primary"><Mail className="w-3.5 h-3.5" /> {lead.email}</a>
                    {lead.phone && <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {lead.phone}</span>}
                    {lead.company && <span className="inline-flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> {lead.company}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select value={lead.status} onChange={(e) => updateStatus(lead.id, e.target.value)} data-testid={`lead-status-${lead.id}`} className="bg-[#05060A] border border-white/10 text-sm text-white px-3 py-2 outline-none focus:border-primary">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => remove(lead.id)} data-testid={`lead-delete-${lead.id}`} className="w-9 h-9 grid place-items-center border border-white/10 text-slate-400 hover:text-red-400 hover:border-red-400/40 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {lead.service && <p className="mt-3 text-sm"><span className="text-slate-500">Interested in:</span> <span className="text-primary">{lead.service}</span></p>}
              <p className="mt-3 text-sm text-slate-300 leading-relaxed border-l-2 border-white/10 pl-4">{lead.message}</p>
              {lead.created_at && <p className="mt-3 text-xs text-slate-600 inline-flex items-center gap-1.5"><Clock className="w-3 h-3" /> {new Date(lead.created_at).toLocaleString()}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
